import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  getInvestmentPerformance,
  getExecutionLogs,
  executeInvestment,
  snapshotInvestment,
  type PerformanceSnapshot,
  type ExecutionLog,
  type InvestmentExecutionResult,
} from "../services/brokerageService";

export function ExecutionPage() {
  const { investmentId } = useParams<{ investmentId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"performance" | "logs">("performance");

  // Performance data
  const [hasData, setHasData] = useState(false);
  const [performance, setPerformance] = useState<{
    current?: PerformanceSnapshot;
    history?: PerformanceSnapshot[];
    total_return?: string;
    total_return_pct?: string;
    portfolio_value?: string;
    positions_count?: number;
  } | null>(null);

  // Execution logs
  const [logs, setLogs] = useState<ExecutionLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);

  // Action states
  const [executing, setExecuting] = useState(false);
  const [snapshotting, setSnapshotting] = useState(false);
  const [actionResult, setActionResult] = useState<InvestmentExecutionResult | null>(null);

  useEffect(() => {
    if (investmentId) {
      loadData();
    }
  }, [investmentId]);

  async function loadData() {
    if (!investmentId) return;

    try {
      setLoading(true);
      setError(null);

      const [perfData, logsData] = await Promise.all([
        getInvestmentPerformance(investmentId),
        getExecutionLogs(investmentId),
      ]);

      setHasData(perfData.has_data);
      if (perfData.has_data) {
        setPerformance({
          current: perfData.current,
          history: perfData.history,
          total_return: perfData.total_return,
          total_return_pct: perfData.total_return_pct,
          portfolio_value: perfData.portfolio_value,
          positions_count: perfData.positions_count,
        });
      }

      setLogs(logsData.logs || []);
      setLogsTotal(logsData.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
    } finally {
      setLoading(false);
    }
  }

  async function handleExecute(dryRun: boolean) {
    if (!investmentId) return;

    try {
      setExecuting(true);
      setError(null);
      setActionResult(null);

      const asyncResp = await executeInvestment(investmentId, dryRun);
      // Show that execution started (async now)
      setActionResult({
        investment_id: investmentId,
        investment_name: "",
        success: true,
        skipped: false,
        intended_trades: [],
        submitted_orders: [],
        shadow_updates: [],
        orders_submitted: 0,
        orders_filled: 0,
        orders_failed: 0,
        market_open: true,
        is_first_execution: false,
        async_run_id: asyncResp.execution_run_id,
      } as InvestmentExecutionResult & { async_run_id: string });

      // Reload logs after a delay to allow background execution
      setTimeout(async () => {
        const logsData = await getExecutionLogs(investmentId);
        setLogs(logsData.logs || []);
        setLogsTotal(logsData.total || 0);
      }, 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Execution failed");
    } finally {
      setExecuting(false);
    }
  }

  async function handleSnapshot() {
    if (!investmentId) return;

    try {
      setSnapshotting(true);
      setError(null);

      await snapshotInvestment(investmentId);
      
      // Reload performance data
      const perfData = await getInvestmentPerformance(investmentId);
      setHasData(perfData.has_data);
      if (perfData.has_data) {
        setPerformance({
          current: perfData.current,
          history: perfData.history,
          total_return: perfData.total_return,
          total_return_pct: perfData.total_return_pct,
          portfolio_value: perfData.portfolio_value,
          positions_count: perfData.positions_count,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Snapshot failed");
    } finally {
      setSnapshotting(false);
    }
  }

  function formatCurrency(value: string | undefined): string {
    if (!value) return "$0.00";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(num);
  }

  function formatPercent(value: string | undefined): string {
    if (!value) return "0.00%";
    const num = parseFloat(value);
    const sign = num >= 0 ? "+" : "";
    return `${sign}${num.toFixed(2)}%`;
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "partial":
        return "bg-yellow-100 text-yellow-800";
      case "failed":
        return "bg-red-100 text-red-800";
      case "running":
        return "bg-blue-100 text-blue-800";
      case "skipped":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }

  if (!investmentId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Investment ID is required</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Strategy Execution"
        subtitle="View performance and execution history"
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/portfolio")}
          className="mb-6 inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
        >
          <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Portfolio
        </button>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {actionResult && (
          <div className={`mb-6 px-4 py-3 rounded-lg border ${
            !actionResult.success && actionResult.error
              ? "bg-red-50 border-red-200 text-red-700" 
              : actionResult.skipped 
                ? "bg-yellow-50 border-yellow-200 text-yellow-700"
                : "bg-green-50 border-green-200 text-green-700"
          }`}>
            {!actionResult.success && actionResult.error && (
              <p><strong>Execution Failed:</strong> {actionResult.error}</p>
            )}
            {actionResult.skipped && (
              <p><strong>Execution Skipped:</strong> {actionResult.skip_reason}</p>
            )}
            {actionResult.success && !actionResult.skipped && (
              <p>
                <strong>Execution Complete:</strong> {actionResult.orders_submitted} orders submitted, {actionResult.orders_filled} filled, {actionResult.orders_failed} failed
              </p>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex flex-wrap gap-3 sm:gap-4">
          <button
            onClick={() => handleExecute(true)}
            disabled={executing}
            className="inline-flex items-center px-4 py-2 border border-indigo-600 text-sm font-medium rounded-md text-indigo-600 bg-white hover:bg-indigo-50 disabled:opacity-50"
          >
            {executing ? "Executing..." : "Dry Run"}
          </button>
          <button
            onClick={() => handleExecute(false)}
            disabled={executing}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {executing ? "Executing..." : "Execute Now"}
          </button>
          <button
            onClick={handleSnapshot}
            disabled={snapshotting}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {snapshotting ? "Capturing..." : "Capture Snapshot"}
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading...</p>
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("performance")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "performance"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Performance
                </button>
                <button
                  onClick={() => setActiveTab("logs")}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "logs"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Execution Logs ({logsTotal})
                </button>
              </nav>
            </div>

            {/* Performance Tab */}
            {activeTab === "performance" && (
              <div>
                {!hasData ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No performance data yet</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Performance data will appear after the first execution or snapshot.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-lg shadow p-5">
                        <p className="text-sm text-gray-500">Portfolio Value</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {formatCurrency(performance?.portfolio_value)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow p-5">
                        <p className="text-sm text-gray-500">Total Return</p>
                        <p className={`text-2xl font-semibold ${parseFloat(performance?.total_return_pct || "0") >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {formatPercent(performance?.total_return_pct)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow p-5">
                        <p className="text-sm text-gray-500">Total Return ($)</p>
                        <p className={`text-2xl font-semibold ${parseFloat(performance?.total_return || "0") >= 0 ? "text-green-600" : "text-red-600"}`}>
                          {formatCurrency(performance?.total_return)}
                        </p>
                      </div>
                      <div className="bg-white rounded-lg shadow p-5">
                        <p className="text-sm text-gray-500">Positions</p>
                        <p className="text-2xl font-semibold text-gray-900">
                          {performance?.positions_count || 0}
                        </p>
                      </div>
                    </div>

                    {/* History Table */}
                    {performance?.history && performance.history.length > 0 && (
                      <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="px-4 py-3 border-b border-gray-200">
                          <h3 className="text-lg font-medium text-gray-900">Performance History</h3>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Day Return</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Return</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Positions</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {performance.history.map((snap) => (
                                <tr key={snap.id}>
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {new Date(snap.snapshot_date).toLocaleDateString()}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                                    {formatCurrency(snap.portfolio_value)}
                                  </td>
                                  <td className={`px-4 py-3 text-sm text-right ${parseFloat(snap.day_return_pct) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                    {formatPercent(snap.day_return_pct)}
                                  </td>
                                  <td className={`px-4 py-3 text-sm text-right ${parseFloat(snap.total_return_pct) >= 0 ? "text-green-600" : "text-red-600"}`}>
                                    {formatPercent(snap.total_return_pct)}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-500">
                                    {snap.positions_count}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Logs Tab */}
            {activeTab === "logs" && (
              <div>
                {logs.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    <h3 className="mt-4 text-lg font-medium text-gray-900">No execution logs yet</h3>
                    <p className="mt-2 text-sm text-gray-500">
                      Logs will appear after the first execution attempt.
                    </p>
                  </div>
                ) : (
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Drift</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Orders</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Duration</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {logs.map((log) => (
                            <tr key={log.id}>
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {new Date(log.execution_date).toLocaleDateString()}{" "}
                                <span className="text-gray-500">
                                  {new Date(log.execution_time).toLocaleTimeString()}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-500 capitalize">
                                {log.execution_type}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(log.status)}`}>
                                  {log.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">
                                {parseFloat(log.max_drift_percent).toFixed(2)}%
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">
                                {(log.orders_executed?.length || 0)}/{(log.orders_planned?.length || 0)}
                              </td>
                              <td className="px-4 py-3 text-sm text-right text-gray-500">
                                {log.duration_ms ? `${log.duration_ms}ms` : "-"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
